import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { AppContext } from '@prisjakt/ui-app-context';
import { Checkbox } from 'src/components/inputs';
import Breadcrumb from '@prisjakt/ui-breadcrumb';

import ViewportContext from '../../../../components/viewport/context';
import ProductFinder from '../product-finder';

import {
  trackComparisonSelectionChanged,
  trackToggleShowDifferences,
} from '../../tracking';

import {
  CategoryPath,
  CategoryPropsFragment,
  Product,
  ProductComparePageQuery,
  RelatedProduct,
} from '../../../../types';

import * as S from './styles';
import {
  trackInternalNavigationClick,
  InternalNavigationScope,
} from 'src/globals/tracking/tracking';

type OwnProps = {
  category: CategoryPropsFragment['category'];
  highlightDifferences: boolean;
  products: NonNullable<ProductComparePageQuery['products']> | Product[];
  relatedProducts: RelatedProduct[];
  setHighlightDifferences: (diff: boolean) => void;
  showSearchInHeader: boolean;
  showSearchInput: boolean;
  toggleSearchInput: () => void;
};

const PageHeader = ({
  category,
  highlightDifferences,
  products,
  relatedProducts,
  setHighlightDifferences,
  toggleSearchInput,
  showSearchInput,
  showSearchInHeader,
}: OwnProps) => {
  const { linkTo, t } = useContext(AppContext);
  const { isDesktop } = useContext(ViewportContext);
  const history = useHistory();
  const searchWrapper = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (
        showSearchInput &&
        searchWrapper.current &&
        !searchWrapper.current.contains(e.target as Node)
      ) {
        toggleSearchInput();
      }
    },
    [showSearchInput, toggleSearchInput]
  );

  useEffect(() => {
    if (isDesktop) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  const showHighlightCheckbox = products.length >= 2;

  const path = [
    {
      id: `productCompare`,
      name: t('category', 'product-compare.breadcrumb'),
      pathName: '',
    },
  ] as CategoryPath[];

  const paths =
    category?.path && Array.isArray(category.path)
      ? ([...path, ...category.path].filter(x => x) as CategoryPath[])
      : path;

  const onSearchSelect = useCallback(
    (product: NonNullable<ProductComparePageQuery['products']> | Product) => {
      toggleSearchInput();
      history.push(
        linkTo('productCompare', {
          category,
          products: [...products, product],
        })
      );

      trackComparisonSelectionChanged(products.length + 1, 'add', 'search');
    },
    [category, history, linkTo, products, toggleSearchInput]
  );

  const handleHighlightDifferences = useCallback(() => {
    setHighlightDifferences(!highlightDifferences);
    trackToggleShowDifferences(!highlightDifferences);
  }, [highlightDifferences, setHighlightDifferences]);

  const getTitle = () => {
    const productsInTitleLength = 2;
    if (!products.length) {
      return (
        <S.Title>{t('category', 'product-compare.title-fallback')}</S.Title>
      );
    }
    const title =
      products.length > 1 ? (
        products
          .map(product => product.name)
          .filter((name, index) => index < productsInTitleLength)
          // Reduce expects to return string but returns a JSX element.
          // Should be fixed when we have time...
          // @ts-ignore
          .reduce((name1, name2) => {
            const countIndicator =
              products.length <= productsInTitleLength
                ? ''
                : `+ ${products.length - productsInTitleLength}`;
            return (
              <S.Title>
                {name1} <S.VersusText>{t('compare', 'title.vs')}</S.VersusText>{' '}
                {name2} {countIndicator}
              </S.Title>
            );
          })
      ) : (
        <S.Title>{products[0].name}</S.Title>
      );

    return title;
  };

  return (
    <>
      <Breadcrumb
        firstPathSelectable={false}
        homeText={t('common', 'breadcrumb.home')}
        path={paths}
        onClick={(nextPage: string) =>
          trackInternalNavigationClick({
            scope: InternalNavigationScope.BREADCRUMB,
            nextPage,
          })
        }
      />
      <S.PageHeader>
        <S.PageHeaderContent>
          {getTitle()}
          <S.ItemsWrapper hasPadding={!showSearchInput}>
            {showHighlightCheckbox && (
              <S.PageHeaderContentItem wide hide={showSearchInput}>
                <Checkbox
                  name="highlightDifferences"
                  label={t('category', 'product-compare.show-differences')}
                  checked={highlightDifferences}
                  onChange={handleHighlightDifferences}
                />
              </S.PageHeaderContentItem>
            )}
            {showSearchInHeader && (
              <S.PageHeaderContentItem
                wide={showSearchInput}
                ref={searchWrapper}
              >
                {showSearchInput && (
                  <ProductFinder
                    focusOnMount
                    onSelect={onSearchSelect}
                    relatedProducts={relatedProducts}
                    showNewComparePageLayout
                    showSearchInHeader
                    showCloseButton
                  />
                )}
                {(isDesktop || showHighlightCheckbox) && (
                  <S.ToggleSearchButton hide={showSearchInput} />
                )}
              </S.PageHeaderContentItem>
            )}
          </S.ItemsWrapper>
        </S.PageHeaderContent>
      </S.PageHeader>
    </>
  );
};

export default PageHeader;
